import { IMsalContext, useMsal } from '@azure/msal-react';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Constants } from '../../../Models/Constants';
import { IMultiSectionUnitConfiguration, IUnitBaseConfiguration } from '../../../Models/IUnitConfiguration';
import { ApiService } from '../../../Services/ApiService';
import { getAppInsights, getFlowTypeUnits, getPreferredUnits, hasRole, setLocalStorageItem } from '../../../Services/Global';
import { getUnitOperationTargetValueName, getPHEType } from "../../../Utils/UnitConfigurationHelper";
import { IRangeValidation } from '../../../Models/IRangeValidation';
import { ConditionalTextFieldInputs } from '../../Atom/ConditionalTextFields/ConditionalTextFieldInputs';
import { IPHEPlateType } from '../../../Models/IPHEPlateType';
import { IMultiSectionProperties } from '../../../Models/IMultiSectionProperties';
import { resolveUnitTypeForProperty } from '../../../Utils/UnitsHelper';
import BeerCoolingWarningImage from '../../../Assets/PNG/beerCoolingWarning.png';
import AditionStepsInfoImage from '../../../Assets/JPEG/CopyXmlInfo.jpeg';
import { ImagePreview } from '../../Atom/image/ImagePreview';
import ALICEinstruction from '../.../../../../Assets/ALICEIntegration/ALICEinstruction.png';
import { initialState, productDesignFormReducer } from './ProductDesignFormTypes';
import { createStyles, cssStyles } from './ProductDesignFormStyles';
import { 
  validateTemperature, 
  isRelevantOutputField, 
  validateFormCompletion, 
  isBeerPasteurizerInfo, 
  isBeerCoolingInfo,
  validateInputFields
} from './ProductDesignFormUtils';
import { 
  submitForm as submitFormApi, 
  copyToClipBoard as copyToClipBoardApi
} from './ProductDesignFormApi';
import { handleFormChange } from './ProductDesignFormEventHandlers';

// Material UI imports
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
  useTheme,
  Alert,
  Tooltip,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { FeedbackForm } from '../Feedback/FeedbackForm';
import { UnitAwareTextField } from '../../Atoms/UnitAwareTextField';

/**
 * ProductDesignForm component - Material UI version
 * Product design form component for creating and calculating heat exchanger designs
 */

export const ProductDesignForm: React.FC<{ showConditionalFields: boolean }> = (props) => {
  let appInsights = getAppInsights();
  let theme = useTheme();
  const { search } = useLocation();
  let query = useMemo(() => new URLSearchParams(search), [search]);
  let navigate = useNavigate();

  // OPTIMIZED: Use memoized styles from external file
  const styles = useMemo(() => createStyles(theme), [theme]);

  const ctx: IMsalContext = useMsal();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);

  const [state, dispatch] = useReducer(productDesignFormReducer, initialState);

  const preferredUnit = getPreferredUnits().preferredUnits;
  const temperatureUnit = preferredUnit === 'us' ? '°F' : '°C';
  const pressureUnit = preferredUnit === 'us' ? 'PSI' : 'kPa';

  const hotFlowRateRange = state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaFlowrate);
  const hotInletRange = state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaTempIn);
  const hotOutletRange = state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaTempOut);
  const coldInletRange = state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaTempIn);

  // Function to calculate real acceptable ranges that satisfy both individual field and temperature relationship validations
  const calculateRealAcceptableRanges = () => {
    const realRanges: any = {
      hotFlowRate: hotFlowRateRange,
      hotInlet: hotInletRange,
      hotOutlet: hotOutletRange,
      hotPressureDrop: state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaPressureDrop),
      coldInlet: coldInletRange,
      coldOutlet: state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaTempOut),
      coldFlowRate: state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaFlowrate),
      coldPressureDrop: state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaPressureDrop)
    };

    // Calculate real acceptable range for ColdMediaTempIn considering temperature relationship validation
    if (coldInletRange && state.rangeValidations && state.hotMediaTempOut && !isNaN(parseFloat(state.hotMediaTempOut.toString()))) {
      const tempApproachHotOutlet = state.rangeValidations.find(x => x.propertyName === 'TempApproachHotOutlet');
      if (tempApproachHotOutlet) {
        const hotMediaTempOut = parseFloat(state.hotMediaTempOut.toString());
        
        // Start with field's original range
        let realMin = coldInletRange.lessThan;
        let realMax = coldInletRange.greaterThan;
        
        // greaterThan defines MINIMUM difference → gives UPPER BOUND for ColdMediaTempIn
        // Condition: HotMediaTempOut - ColdMediaTempIn ≥ greaterThan
        // Which means: ColdMediaTempIn ≤ HotMediaTempOut - greaterThan
        if (tempApproachHotOutlet.greaterThan != null && tempApproachHotOutlet.greaterThan !== 0) {
          const upperBound = hotMediaTempOut - tempApproachHotOutlet.greaterThan;
          if (realMax != null) {
            realMax = Math.min(realMax, upperBound);
          } else {
            realMax = upperBound;
          }
        }
        
        // lessThan defines MAXIMUM difference → gives UPPER BOUND for ColdMediaTempIn
        // Condition: HotMediaTempOut - ColdMediaTempIn > lessThan (difference must be greater than lessThan)
        // Which means: ColdMediaTempIn < HotMediaTempOut - lessThan
        // So this sets an UPPER BOUND (not a lower bound!)
        if (tempApproachHotOutlet.lessThan != null && tempApproachHotOutlet.lessThan !== 0) {
          const upperBoundFromLessThan = hotMediaTempOut - tempApproachHotOutlet.lessThan;
          if (realMax != null) {
            realMax = Math.min(realMax, upperBoundFromLessThan);
          } else {
            realMax = upperBoundFromLessThan;
          }
        }
        
        // Only apply enhanced range if valid
        if (realMin != null && realMax != null && !isNaN(realMin) && !isNaN(realMax) && realMin <= realMax) {
          realRanges.coldInlet = {
            ...coldInletRange,
            realAcceptableMin: realMin,
            realAcceptableMax: realMax,
            hasTemperatureConstraint: true
          };
        }
      }
    }

    // Calculate real acceptable range for ColdMediaTempOut considering temperature relationship validation
    if (state.rangeValidations && state.hotMediaTempIn && !isNaN(parseFloat(state.hotMediaTempIn.toString()))) {
      const coldOutletRange = state.rangeValidations.find(v => v.propertyName === Constants.ColdMediaTempOut);
      const tempApproachHotInlet = state.rangeValidations.find(x => x.propertyName === 'TempApproachHotInlet');
      
      if (coldOutletRange && tempApproachHotInlet) {
        const hotMediaTempIn = parseFloat(state.hotMediaTempIn.toString());
        
        // Start with field's original range
        let realMin = coldOutletRange.lessThan;
        let realMax = coldOutletRange.greaterThan;
        
        // greaterThan defines MINIMUM difference → gives UPPER BOUND for ColdMediaTempOut
        // Condition: HotMediaTempIn - ColdMediaTempOut ≥ greaterThan
        // Which means: ColdMediaTempOut ≤ HotMediaTempIn - greaterThan
        if (tempApproachHotInlet.greaterThan != null && tempApproachHotInlet.greaterThan !== 0) {
          const upperBound = hotMediaTempIn - tempApproachHotInlet.greaterThan;
          if (realMax != null) {
            realMax = Math.min(realMax, upperBound);
          } else {
            realMax = upperBound;
          }
        }
        
        // lessThan defines MAXIMUM difference → gives UPPER BOUND for ColdMediaTempOut
        // Condition: HotMediaTempIn - ColdMediaTempOut > lessThan (difference must be greater than lessThan)
        // Which means: ColdMediaTempOut < HotMediaTempIn - lessThan
        // So this sets an UPPER BOUND (not a lower bound!)
        if (tempApproachHotInlet.lessThan != null && tempApproachHotInlet.lessThan !== 0) {
          const upperBoundFromLessThan = hotMediaTempIn - tempApproachHotInlet.lessThan;
          if (realMax != null) {
            realMax = Math.min(realMax, upperBoundFromLessThan);
          } else {
            realMax = upperBoundFromLessThan;
          }
        }
        
        // Only apply enhanced range if valid
        if (realMin != null && realMax != null && !isNaN(realMin) && !isNaN(realMax) && realMin <= realMax) {
          realRanges.coldOutlet = {
            ...coldOutletRange,
            realAcceptableMin: realMin,
            realAcceptableMax: realMax,
            hasTemperatureConstraint: true
          };
        }
      }
    }

    return realRanges;
  };

  const realAcceptableRanges = calculateRealAcceptableRanges();


  // OPTIMIZED: Memoize expensive string processing operations
  const processedQuery = useMemo(() => {
    const process = query.get('process') as string;
    if (!process) return '';
    const singleDecodedString = decodeURIComponent(process);
    return decodeURIComponent(singleDecodedString);
  }, [query]);

  const isBeerCooling = useMemo(() => {
    return processedQuery?.toLowerCase().indexOf(Constants.BEER_COOLING) > -1;
  }, [processedQuery]);

  const isBeerPasteurizer = useMemo(() => {
    return processedQuery?.toLowerCase().indexOf(Constants.BEER_PASTEURIZER) > -1;
  }, [processedQuery]);


  // OPTIMIZED: Memoize help tooltip component
  const helpTooltipComponent = useMemo(() => (
    <Box>
      <Box sx={styles.helpTooltipContainer}>
        <Box component="img" sx={cssStyles.helpTooltipImage} src={ALICEinstruction} alt="How to use copied content in Alice" />
      </Box>
      <Typography variant="caption" sx={styles.helpTooltipText}>
        After clicking Copy, return to configurator and use the content using "Paste from CAS".
      </Typography>
    </Box>
  ), [styles.helpTooltipContainer, styles.helpTooltipText, cssStyles.helpTooltipImage]);

  useEffect(() => {
    if (!state.pageland) {
      dispatch({ type: 'SET_PAGELAND', payload: true });
      dispatch({ type: 'SET_DISABLE_COPY_BUTTON', payload: false });
      appInsights?.trackPageView({ name: 'ProductDesignForm' });
    }
  });

  useEffect(() => {
    // Only validate if user has started entering data (firstInteraction is set)
    // This prevents showing validation errors when the form is empty
    if (state.firstInteraction === undefined) {
      // Clear any existing errors if user hasn't started entering data
      dispatch({ type: 'SET_TEMPERATURE_ERROR', payload: [] });
      dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: false });
      return;
    }
    
    const temperatureErrors = validateTemperature(state.inputValues, state.rangeValidations, state.unitConfiguration);
    dispatch({ type: 'SET_TEMPERATURE_ERROR', payload: temperatureErrors });
    
    // Update formHasErrors state to reflect current validation status
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const hasErrors = validateInputFields({ ...state, temperatureError: temperatureErrors }, false);
    dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: hasErrors });
  }, [
    state.inputValues, 
    state.firstInteraction, 
    state.rangeValidations, 
    state.unitConfiguration,
    state.coldMediaTempInError,
    state.coldMediaTempOutError,
    state.coldMediaFlowrateError,
    state.coldMediaPressureDropError,
    state.hotMediaTempInError,
    state.hotMediaTempOutError,
    state.hotMediaFlowrateError,
    state.hotMediaPressureDropError,
    state.userInputs,
    dispatch
  ]);


  useEffect(() => {
    setLocalStorageItem('productdesign', `/productdesign?process=${query.get('process') as string}&productline=${query.get('productline') as string}`);

    // Set loading state before resetting and loading new configuration
    dispatch({ type: 'SET_INITIAL_LOADING', payload: true });

    // Reset all form values before loading new configuration to prevent stale values from previous use case
    dispatch({ type: 'RESET_FORM_VALUES' });

    // Get the unit configuration from the API
    async function getUnitConfigurationAsync() {
      const response = await apiService.getAsync(`settings/unitconfiguration/${query.get('process') as string}/${query.get('productline') as string}`);
      if (!response.ok) {
        throw new Error(`Could not get unit configuration from API. ${response.status} - ${response.statusText}`);
      }
      const result: IUnitBaseConfiguration = await response.json();
      dispatch({ type: 'SET_UNIT_CONFIGURATION', payload: result });

      const sectionResult = await getSectionsAsync(result);

      await getRangeValidations(result, sectionResult);
      await getPHETypes();
      dispatch({ type: 'SET_FLOW_UNIT', payload: getFlowTypeUnits(result.flowType!)});
    }

    // Get the PHE types from the API
    async function getPHETypes() {
      const response = await apiService.getAsync(`settings/pheplatetypes`);
      if (!response.ok) {
        throw new Error(`Could not get PHE types. ${response.status} ${response.statusText}`);
      }
      const pheTypes: IPHEPlateType[] = await response.json();
      dispatch({ type: 'SET_PHE_TYPES', payload: pheTypes });
    }

    // Get the range validations from the API
    async function getRangeValidations(unitInput: IUnitBaseConfiguration, sections: IMultiSectionUnitConfiguration[]) {
      const response = await apiService.getAsync(`settings/rangeValidation`, unitInput);
      if (!response.ok) {
        throw new Error(`Could not get range configurations from API. ${response.status} - ${response.statusText}`);
      }
      const result = await response.json();
      
      // Debug logging for range validations
      if (unitInput.isMultiSection && result && Array.isArray(result)) {
        console.log('[ProductDesignForm] Range validations received:', {
          count: result.length,
          propertyNames: result.map((r: IRangeValidation) => r.propertyName),
          ranges: result
        });
      }
      
      dispatch({ type: 'SET_RANGE_VALIDATIONS', payload: result });
      setDefaultValues(result);
      if (unitInput.isMultiSection) {
        await getPropertySpecs(unitInput, sections, result);
      }

      dispatch({ type: 'SET_INITIAL_LOADING', payload: false });
    }

    function getPropRange(propName: string, propRanges: IRangeValidation[]) {
      return (propRanges?.find(v => v.propertyName === propName) as IRangeValidation);
    }

    // Set default values for the fields if they are not already set in the range validation object
    async function setDefaultValues(propRanges: IRangeValidation[]) {
      if (propRanges !== undefined && propRanges.length > 0) {
        const coldMediaTempOutRange: IRangeValidation = getPropRange(Constants.ColdMediaTempOut, propRanges);
        if (coldMediaTempOutRange !== undefined && coldMediaTempOutRange.greaterThan === coldMediaTempOutRange.lessThan) {
          dispatch({ type: 'SET_COLD_MEDIA_TEMP_OUT', payload: coldMediaTempOutRange.greaterThan });
        }
        let readOnlies: string[] = [];
        const coldMediaTempInRange: IRangeValidation = getPropRange(Constants.ColdMediaTempIn, propRanges);
        if (coldMediaTempInRange !== undefined && coldMediaTempInRange.greaterThan === coldMediaTempInRange.lessThan) {
          dispatch({ type: 'SET_COLD_MEDIA_TEMP_IN', payload: coldMediaTempInRange.greaterThan });
          readOnlies.push(Constants.ColdMediaTempIn);
        }

        const coldMediaFlowrateRange: IRangeValidation = getPropRange(Constants.ColdMediaFlowrate, propRanges);
        if (coldMediaFlowrateRange !== undefined && coldMediaFlowrateRange.greaterThan === coldMediaFlowrateRange.lessThan) {
          dispatch({ type: 'SET_COLD_MEDIA_FLOWRATE', payload: coldMediaFlowrateRange.greaterThan });
          readOnlies.push(Constants.ColdMediaFlowrate);
        }

        const coldMediaPressureDropRange: IRangeValidation = getPropRange(Constants.ColdMediaPressureDrop, propRanges);
        if (coldMediaPressureDropRange !== undefined && coldMediaPressureDropRange.greaterThan === coldMediaPressureDropRange.lessThan) {
          dispatch({ type: 'SET_COLD_MEDIA_PRESSURE_DROP', payload: coldMediaPressureDropRange.greaterThan });
          readOnlies.push(Constants.ColdMediaPressureDrop);
        }

        const hotMediaTempOutRange: IRangeValidation = getPropRange(Constants.HotMediaTempOut, propRanges);
        if (hotMediaTempOutRange !== undefined && hotMediaTempOutRange.greaterThan === hotMediaTempOutRange.lessThan) {
          dispatch({ type: 'SET_HOT_MEDIA_TEMP_OUT', payload: hotMediaTempOutRange.greaterThan });
          readOnlies.push(Constants.HotMediaTempOut);
        }

        const hotMediaTempInRange: IRangeValidation = getPropRange(Constants.HotMediaTempIn, propRanges);
        if (hotMediaTempInRange !== undefined && hotMediaTempInRange.greaterThan === hotMediaTempInRange.lessThan) {
          dispatch({ type: 'SET_HOT_MEDIA_TEMP_IN', payload: hotMediaTempInRange.greaterThan });
          readOnlies.push(Constants.HotMediaTempIn);
        }

        const hotMediaFlowrateRange: IRangeValidation = getPropRange(Constants.HotMediaFlowrate, propRanges);
        if (hotMediaFlowrateRange !== undefined && hotMediaFlowrateRange.greaterThan === hotMediaFlowrateRange.lessThan) {
          dispatch({ type: 'SET_HOT_MEDIA_FLOWRATE', payload: hotMediaFlowrateRange.greaterThan });
          readOnlies.push(Constants.HotMediaFlowrate);
        }

        const hotMediaPressureDropRange: IRangeValidation = getPropRange(Constants.HotMediaPressureDrop, propRanges);
        if (hotMediaPressureDropRange !== undefined && hotMediaPressureDropRange.greaterThan === hotMediaPressureDropRange.lessThan) {
          dispatch({ type: 'SET_HOT_MEDIA_PRESSURE_DROP', payload: hotMediaPressureDropRange.greaterThan });
          readOnlies.push(Constants.HotMediaPressureDrop);
        }

        dispatch({ type: 'SET_READ_ONLY_FIELDS', payload: readOnlies });
        
        // Synchronize inputValues with state values after setting defaults
        // This ensures inputValues only contains read-only values that were set by setDefaultValues
        const syncedInputValues: { [key: string]: any } = {};
        if (coldMediaTempOutRange !== undefined && coldMediaTempOutRange.greaterThan === coldMediaTempOutRange.lessThan) {
          syncedInputValues[Constants.ColdMediaTempOut] = coldMediaTempOutRange.greaterThan;
        }
        if (coldMediaTempInRange !== undefined && coldMediaTempInRange.greaterThan === coldMediaTempInRange.lessThan) {
          syncedInputValues[Constants.ColdMediaTempIn] = coldMediaTempInRange.greaterThan;
        }
        if (coldMediaFlowrateRange !== undefined && coldMediaFlowrateRange.greaterThan === coldMediaFlowrateRange.lessThan) {
          syncedInputValues[Constants.ColdMediaFlowrate] = coldMediaFlowrateRange.greaterThan;
        }
        if (coldMediaPressureDropRange !== undefined && coldMediaPressureDropRange.greaterThan === coldMediaPressureDropRange.lessThan) {
          syncedInputValues[Constants.ColdMediaPressureDrop] = coldMediaPressureDropRange.greaterThan;
        }
        if (hotMediaTempOutRange !== undefined && hotMediaTempOutRange.greaterThan === hotMediaTempOutRange.lessThan) {
          syncedInputValues[Constants.HotMediaTempOut] = hotMediaTempOutRange.greaterThan;
        }
        if (hotMediaTempInRange !== undefined && hotMediaTempInRange.greaterThan === hotMediaTempInRange.lessThan) {
          syncedInputValues[Constants.HotMediaTempIn] = hotMediaTempInRange.greaterThan;
        }
        if (hotMediaFlowrateRange !== undefined && hotMediaFlowrateRange.greaterThan === hotMediaFlowrateRange.lessThan) {
          syncedInputValues[Constants.HotMediaFlowrate] = hotMediaFlowrateRange.greaterThan;
        }
        if (hotMediaPressureDropRange !== undefined && hotMediaPressureDropRange.greaterThan === hotMediaPressureDropRange.lessThan) {
          syncedInputValues[Constants.HotMediaPressureDrop] = hotMediaPressureDropRange.greaterThan;
        }
        dispatch({ type: 'SET_INPUT_VALUES', payload: syncedInputValues });
      }
    }

    async function getSectionsAsync(unitConfiguration: IUnitBaseConfiguration): Promise<IMultiSectionUnitConfiguration[]> {
      const response = await apiService.getAsync(`settings/unitconfigurations/sections`, unitConfiguration);
      if (!response.ok) {
        throw new Error("Couldn't get sections for current unit configuration.");
      }

      const data: IMultiSectionUnitConfiguration[] = await response.json();

      const sortedData = data.sort((a, b) => b.sectionNumber! - a.sectionNumber!)
      dispatch({ type: 'SET_SECTION_COLLECTION', payload: sortedData });
      dispatch({ type: 'SET_AMOUNT_OF_SECTIONS', payload: sortedData.length });
      dispatch({ type: 'SET_SECTION', payload: sortedData[0] });

      return sortedData;
    }

    async function getPropertySpecs(unitConfiguration: IMultiSectionUnitConfiguration, sections: IMultiSectionUnitConfiguration[], rangeValidation: IRangeValidation[]) {
      let propData: any = {};

      const propResponse = await apiService.getAsync(`settings/unitPropertySpecifications`, unitConfiguration);
      try {
        propData = await propResponse.json();
      } catch (error) {

      }

      if (!propResponse.ok) {
        dispatch({ type: 'SET_IS_LOADING', payload: false });
        throw new Error("Couldn't get unit property specifications for current unit configuration.");
      }

      // Declare the multiSecProps variable with the correct type
      let multiSecProps: IMultiSectionProperties[] = [];
      
      // Track read-only fields for multi-section (properties with fixed values where greaterThan === lessThan)
      let readOnlyFieldsForMultiSection: string[] = [];
      
      // Ensure rangeValidation is an array to prevent errors
      const safeRangeValidation = Array.isArray(rangeValidation) ? rangeValidation : [];
      
      if (propData && propData.userInputProperties && propData.userInputProperties.length > 0) {
        // Debug: Log userInputProperties to understand what we're working with
        console.log('[ProductDesignForm] Processing userInputProperties:', {
          properties: propData.userInputProperties,
          rangeValidationCount: safeRangeValidation.length
        });
        
        // Iterate over userinput properties
        propData.userInputProperties.forEach((prop: string) => {       
          if (prop !== Constants.UnitOperationTargetValue) {
            const match = prop.match(/Sec(\d+)/);
            let trimmedProp = prop.replace(/Sec\d+$/, '');
            const sectionNumber = match ? match![1] : '';
            //check if the pro
            let inputType = prop.toLowerCase().startsWith("hot") ? "hot" : "cold";
            let mediaType = inputType === 'hot' ?
              sections.filter(x => x.sectionNumber === parseInt(sectionNumber))[0].hotMediaType
              :
              sections.filter(x => x.sectionNumber === parseInt(sectionNumber))[0].coldMediaType;

            const mappings: { [key: string]: string } = {
              'TempIn': 'temperature inlet',
              'TempOut': 'temperature outlet',
              'Flowrate': 'flowrate',
              'PressureDrop': 'pressure drop'
            };

            let property = prop;

            for (const key in mappings) {
              if (prop.includes(key)) {
                property = `${mappings[key]}`;
                break;
              }
            }

            // Enhanced property name matching with fallbacks
            // First try exact match (e.g., "HotMediaTempInSec1")
            let rangeData = safeRangeValidation.find(v => v.propertyName === prop);
            
            // Fallback: Try case-insensitive match
            if (!rangeData) {
              rangeData = safeRangeValidation.find(v => 
                v.propertyName?.toLowerCase() === prop.toLowerCase()
              );
            }
            
            // Fallback: Try trimmed property name match (base property without section)
            // This helps when backend stores ranges by base property name
            if (!rangeData && trimmedProp !== prop) {
              rangeData = safeRangeValidation.find(v => 
                v.propertyName === trimmedProp || 
                v.propertyName?.toLowerCase() === trimmedProp.toLowerCase()
              );
            }

            // Debug logging for unmatched properties
            if (!rangeData) {
              console.warn(`[ProductDesignForm] No range validation found for property: ${prop}`, {
                searchedFor: [prop, prop.toLowerCase(), trimmedProp],
                availableRanges: safeRangeValidation.map((r: IRangeValidation) => r.propertyName)
              });
            }

            let multiProperty: IMultiSectionProperties = {
              property: property,
              sectionNumber: parseInt(sectionNumber),
              name: prop,
              trimmedName: trimmedProp,
              flowUnit: '',
              mediaType: mediaType ? mediaType : '',
              errorMessage: '',
              greaterThan: rangeData?.greaterThan,
              lessThan: rangeData?.lessThan,
              isDecimal: rangeData?.isDecimal ?? true,
              // The label is irrelevant on this branch: the enclosing `if`
              // excludes the unit operation target, so resolution is always by
              // property name here.
              unitType: resolveUnitTypeForProperty(prop, undefined, unitConfiguration.flowType)
            }

            if (prop.toLowerCase().includes('flow')) {
              multiProperty.flowUnit = getFlowTypeUnits(unitConfiguration.flowType!)
            } else if (prop.toLowerCase().includes('temp')) {
              multiProperty.flowUnit = temperatureUnit;
            }

            // Set default value and mark as read-only if greaterThan === lessThan (fixed value)
            // This matches the behavior for single-section forms in setDefaultValues
            if (rangeData !== undefined && rangeData.greaterThan === rangeData.lessThan) {
              multiProperty.value = rangeData.greaterThan.toString();
              // Add to read-only fields list (use full property name with Sec suffix, e.g., "ColdMediaTempOutSec1")
              readOnlyFieldsForMultiSection.push(prop);
            }

            multiSecProps.push(multiProperty);
          }
          else {
            // Enhanced property name matching for UnitOperationTargetValue
            // Note: safeRangeValidation is already defined in outer scope
            let rangeData = safeRangeValidation.find(v => v.propertyName === prop);
            if (!rangeData) {
              rangeData = safeRangeValidation.find(v => 
                v.propertyName?.toLowerCase() === prop.toLowerCase()
              );
            }

            let multiProperty: IMultiSectionProperties = {
              property: sections[0].unitOperationTargetName !== undefined ? getUnitOperationTargetValueName(sections[0].unitOperationTargetName) : "Unit Operation Target Value",
              sectionNumber: 0,
              name: Constants.UnitOperationTargetValue,
              trimmedName: Constants.UnitOperationTargetValue,
              flowUnit: sections[0].unitOperationTargetValueUnit !== undefined ? sections[0].unitOperationTargetValueUnit : 'Integer',
              mediaType: '',
              errorMessage: '',
              greaterThan: rangeData?.greaterThan,
              lessThan: rangeData?.lessThan,
              // The target can carry any quantity, so it is resolved from the
              // configured unit label rather than from its property name.
              unitType: resolveUnitTypeForProperty(
                Constants.UnitOperationTargetValue,
                sections[0].unitOperationTargetValueUnit,
                unitConfiguration.flowType
              )
            }
            multiSecProps.push(multiProperty);
          }
        });

        dispatch({ type: 'SET_USER_INPUTS', payload: multiSecProps });
        
        // Update read-only fields for multi-section forms
        // This will be merged with existing read-only fields in the reducer
        if (readOnlyFieldsForMultiSection.length > 0) {
          dispatch({ type: 'SET_READ_ONLY_FIELDS', payload: readOnlyFieldsForMultiSection });
        }
      }
    }

    dispatch({ type: 'SET_WARNING_MESSAGE', payload: <></> });
    dispatch({ type: 'SET_SHOW_WARNING_FOR_PHE', payload: false });

    getUnitConfigurationAsync();

    localStorage.removeItem('modelSuggestion');
  }, [query])

  // OPTIMIZED: Event handler moved to ProductDesignFormEventHandlers.ts
  const handleChange = (e: React.FormEvent<HTMLInputElement> | any) => {
    handleFormChange(e, state, dispatch);
  };

  // OPTIMIZED: Moved to ProductDesignFormApi.ts
  const submitForm = useCallback(() => {
    submitFormApi(state, dispatch, apiService, query, processedQuery);
  }, [state, dispatch, apiService, query, processedQuery]);

  // OPTIMIZED: copyToClipBoard moved to ProductDesignFormApi.ts
  const copyToClipBoard = () => copyToClipBoardApi(state, dispatch, apiService);

  // Add keyboard event listener for Enter key to trigger calculate
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Enter key is pressed and we're inside the form
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement;
        // Only handle if the target is an input field within our form
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
          const formElement = document.getElementById('designForm');
          if (formElement && formElement.contains(target)) {
            // Check if Calculate button would be active (same conditions as button's disabled state)
            const isCalculateButtonActive = !state.disableSubmit && 
                                           !state.isLoading && 
                                           validateFormCompletion() && // Form IS completely filled
                                           !state.formHasErrors; // No validation errors exist
            
            // Only proceed if the button would be active and we have a unit configuration
            if (isCalculateButtonActive && state.unitConfiguration) {
              // Prevent default form submission behavior
              event.preventDefault();
              event.stopPropagation();
              
              // Execute the same logic as the Calculate button's onClick
              submitForm();
            }
          }
        }
      }
    };

    // Add event listener to document to catch all Enter key presses
    document.addEventListener('keydown', handleKeyDown);
    
    // Always return cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    state.disableSubmit, 
    state.isLoading, 
    state.formHasErrors, 
    state.unitConfiguration,
    state.userInputs, // Important for multi-section validation
    submitForm
  ]); // Dependencies that affect button state

  // OPTIMIZED: iosCopyToClipboard moved to ProductDesignFormApi.ts

  // Determine if side panel should be shown and what content to display
  const sidePanelContent = useMemo(() => {
    // Case 1: Beer Pasteurizer - Additional Steps (after calculate)
    if (isBeerPasteurizerInfo(processedQuery) && state.designReceived) {
      return {
        show: true,
        title: 'Additional steps',
        htmlContent: Constants.ADDITIONAL_STEPS_HTML,
        image: AditionStepsInfoImage,
        severity: 'info' as const
      };
    }
    
    // Case 2: Beer Cooling - Warning (after calculate)
    if (isBeerCoolingInfo(processedQuery) && state.designReceived) {
      return {
        show: true,
        title: 'Calculation warning',
        htmlContent: Constants.WARNING_BEER_COOLING_HTML,
        image: BeerCoolingWarningImage,
        severity: 'warning' as const
      };
    }
    
    // Case 3: Beer Pasteurizer - Information (before calculate, when showInformation is true)
    if (isBeerPasteurizerInfo(processedQuery) && state.showInformation) {
      return {
        show: true,
        title: 'Calculation information',
        htmlContent: Constants.WARNING_BEER_PASTEURIZER_HTML,
        image: null,
        severity: 'info' as const
      };
    }
    
    // Case 4: Custom informationForUser from API (when xmlMessage is set and not usable in ALICE)
    // NOTE: This was moved back to original alert location as requested by user
    /*
    if (state.showXmlMessage && state.xmlMessage && !state.isXmlUsable) {
      return {
        show: true,
        title: 'Calculation information',
        htmlContent: `<p>${state.xmlMessage}</p>`,
        image: null,
        severity: 'warning' as const
      };
    }
    */
    
    // Case 5: PHE Warning Message
    // NOTE: This was moved back to original alert location as requested by user
    /*
    if (state.showWarningForPHE && state.warningMessage) {
      return {
        show: true,
        title: 'Calculation information',
        htmlContent: `<p>${state.warningMessage}</p>`,
        image: null,
        severity: 'warning' as const
      };
    }
    */
    
    return {
      show: false,
      title: '',
      htmlContent: '',
      image: null,
      severity: 'info' as const
    };
  }, [
    processedQuery,
    state.designReceived,
    state.showInformation
    // state.showXmlMessage, // Removed from dependencies
    // state.xmlMessage,     // Removed from dependencies
    // state.isXmlUsable,    // Removed from dependencies
    // state.showWarningForPHE, // Removed from dependencies
    // state.warningMessage  // Removed from dependencies
  ]);

  const showSidePanel = sidePanelContent.show;

  // Handle collapsible sections in HTML content
  useEffect(() => {
    if (!showSidePanel) return;

    const handleCollapsibleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && target.classList.contains('collapsible')) {
        target.classList.toggle('collapsed');
        const content = target.nextElementSibling as HTMLElement;
        if (content && content.classList.contains('collapsedContent')) {
          content.classList.toggle('collapsed');
        }
      }
    };

    const sidePanelElement = document.querySelector('[data-side-panel]');
    if (sidePanelElement) {
      sidePanelElement.addEventListener('click', handleCollapsibleClick);
      return () => {
        sidePanelElement.removeEventListener('click', handleCollapsibleClick);
      };
    }
    return undefined;
  }, [showSidePanel, sidePanelContent.htmlContent]);

  if (!hasRole(...['Alice'])) {
    return (
      <Box sx={styles.permissionsContainer}>
        <Typography variant="h6" color="text.secondary">
          You don't have the right permissions to access this tool.
        </Typography>
      </Box>
    )
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{
        ...styles.container,
        maxWidth: showSidePanel ? '1800px' : '800px',
        px: 3,
        display: 'flex',
        justifyContent: 'center',
        
      }}
    >
      <Box 
        sx={{ 
          display: 'flex',
          justifyContent: 'center',          flexWrap: 'wrap',
          flexDirection: 'row-reverse',
          minHeight: 'min-content',
          py: 2,
          gap: '30px'
        }}
      >
        {/* Side Panel for Additional Steps, Warnings, and Information */}
        {showSidePanel && (
          <Box sx={{ maxWidth: '500px', flexShrink: 0 }}>
            <style>{cssStyles.collapsibleStyles}</style>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3, 
                position: 'sticky', 
                alignSelf: 'flex-start',
                backgroundColor: '#fafafa',
                borderLeft: sidePanelContent.severity === 'warning' ? '4px solid #ed6c02' : '4px solid #1976d2'
              }}
            >
              <Typography 
                variant="h4" 
                gutterBottom 
                color={sidePanelContent.severity === 'warning' ? 'warning' : 'primary'} 
                sx={{ fontWeight: 600 }}
              >
                {sidePanelContent.title}
              </Typography>
              <Box 
                sx={{ 
                  mb: 2,
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  '& p': {
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    marginBottom: '0.5rem'
                  },
                  '& ol, & ul': {
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    paddingLeft: '1.5rem',
                    '& li': {
                      fontSize: '0.85rem',
                      lineHeight: 1.4,
                      marginBottom: '0.25rem'
                    }
                  },
                  '& h3, & h4, & h5, & h6': {
                    fontSize: '0.85rem',
                    lineHeight: 1.4
                  }
                }} 
                data-side-panel
              >
                <div dangerouslySetInnerHTML={{ __html: sidePanelContent.htmlContent }} />
              </Box>
              {sidePanelContent.image && (
                <Box>
                  <Box 
                    component="img" 
                    src={sidePanelContent.image} 
                    sx={{ 
                      width: '100%', 
                      height: 'auto', 
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      boxShadow: 1
                    }} 
                    alt={sidePanelContent.title} 
                  />
                  <Box sx={styles.imagePreviewContainer}>
                    <ImagePreview imageSource={sidePanelContent.image} />
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        <Box sx={{flexShrink: 0, maxWidth: showSidePanel ? '550px' : '800px'}}>
          <Box sx={{ ...styles.mainBox, overflow: 'visible' }}>
        {state.initialLoading && (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={styles.loadingText}>Loading...</Typography>
          </Box>
        )}

        {!state.initialLoading && !state.unitConfiguration && (
          <Box sx={styles.noUnitContainer}>
            <Typography variant="h5" sx={styles.noUnitTitle}>No unit found</Typography>
            <Typography variant="body1" sx={styles.noUnitText}>
              Process: {query.get('process')}
            </Typography>
            <Typography variant="body1" sx={styles.noUnitText}>
              Product Line: {query.get('productline')}
            </Typography>
          </Box>
        )}

        <div>
          {/* Only render form when range validations are loaded (and userInputs for multi-section) */}
          {state.unitConfiguration && state.rangeValidations && (!state.unitConfiguration.isMultiSection || state.userInputs) && (
            <Paper sx={styles.productInfoPaper}>
              <Box sx={styles.productInfoHeader}>
                <Box sx={styles.productTitleSection}>
                  <Typography variant="h4" sx={styles.productTitle}>
                    {state.unitConfiguration.alfaLavalPosition}
                  </Typography>
                  <Typography variant="body1" sx={styles.productLine}>
                    {state.unitConfiguration.productLine}
                  </Typography>
                </Box>
                <Box sx={styles.mediaChipsContainer}>
                  <Chip 
                    label={`Hot media: ${state.unitConfiguration.hotMediaType}`}
                    sx={styles.hotMediaChip}
                  />
                  <Chip 
                    label={`Cold media: ${state.unitConfiguration.coldMediaType}`}
                    sx={styles.coldMediaChip}
                  />
                </Box>
              </Box>

              <form id="designForm" key={`form-${query.get('process')}-${query.get('productline')}`}>
                <ConditionalTextFieldInputs
                  key={`inputs-${query.get('process')}-${query.get('productline')}`}
                  formKey={`${query.get('process')}-${query.get('productline')}`}
                  handleChange={handleChange}
                  originPage={Constants.ProductDesignForm}
                  propertySpecs={state.userInputs!}
                  unitConfiguration={state.unitConfiguration}
                  coldMediaFlowrate={state.coldMediaFlowrate?.toString() || ''}
                  coldMediaTempIn={state.coldMediaTempIn?.toString() || ''}
                  coldMediaTempOut={state.coldMediaTempOut?.toString() || ''}
                  coldMediaPressureDrop={state.coldMediaPressureDrop?.toString() || ''}
                  hotMediaFlowrate={state.hotMediaFlowrate?.toString() || ''}
                  hotMediaTempIn={state.hotMediaTempIn?.toString() || ''}
                  hotMediaTempOut={state.hotMediaTempOut?.toString() || ''}
                  hotMediaPressureDrop={state.hotMediaPressureDrop?.toString() || ''}
                  coldMediaTempInError={state.coldMediaTempInError}
                  hotMediaFlowrateError={state.hotMediaFlowrateError}
                  hotMediaTempInError={state.hotMediaTempInError}
                  hotMediaTempOutError={state.hotMediaTempOutError}
                  hotMediaPressureDropError={state.hotMediaPressureDropError}
                  coldMediaFlowrateError={state.coldMediaFlowrateError}
                  coldMediaTempOutError={state.coldMediaTempOutError}
                  coldMediaPressureDropError={state.coldMediaPressureDropError}
                  temperatureError={state.temperatureError}
                  readOnlyFields={state.readOnlyfields}
                  addStyling={true}
                  valuesRange={realAcceptableRanges}
                />
              </form>
            </Paper>
          )}
        </div>

        {/* Loading Shimmer */}
        {state.isLoading && (
          <Paper sx={styles.shimmerPaper}>
            <Box sx={styles.shimmerBox}>
              <Skeleton variant="text" width="30%" height={20} />
              <Skeleton variant="rectangular" width="100%" height={40} />
            </Box>
            <Box sx={styles.shimmerBox}>
              <Skeleton variant="text" width="40%" height={20} />
              <Skeleton variant="rectangular" width="100%" height={40} />
            </Box>
          </Paper>
        )}

        {/* Results */}
        {state.responseReceived && !state.isLoading && (
          <Paper sx={styles.resultsPaper}>
            <Box sx={styles.resultsFieldContainer}>
              <Typography variant="subtitle2" sx={styles.resultsLabel}>PHE Type</Typography>
              <TextField 
                fullWidth 
                value={getPHEType(state.designSession![0].phePlateType)} 
                InputProps={{ readOnly: true }}
                size="small"
              />
            </Box>
            {state.designSession.map((section) => (
              <Box key={section.sectionNumber} sx={{marginBottom: theme.spacing(0.5)}}>
                <Typography variant="subtitle2" sx={styles.resultsLabel}>
                  Plate Pack Configuration{state.designSession.length > 1 ? ` Section ${section.sectionNumber}` : ''}
                </Typography>
                <TextField 
                  fullWidth 
                  value={section.platePackConfiguration} 
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Box>
            ))}

            {props.showConditionalFields && (
              <Box>
                {state.haveHotMediaOutputs && (
                  <Box sx={styles.hotMediaOutputs}>
                    <Typography variant="subtitle2" sx={styles.outputSectionTitle}>
                      {state.designSession![0].hotMediaType!} Outputs
                    </Typography>
                    {isRelevantOutputField(Constants.HotMediaFlowrate, state.unitConfiguration) && (
                      <Box sx={styles.outputFieldContainer}>
                        <Typography variant="caption">Flow rate</Typography>
                        <UnitAwareTextField 
                          unitType={state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow'}
                          fullWidth 
                          metricValue={state.designSession![0].hotMediaFlowrate} 
                          InputProps={{ 
                            readOnly: true,
                            endAdornment: state.flowUnit 
                          }}
                          size="small"
                        />
                      </Box>
                    )}
                    {isRelevantOutputField(Constants.HotMediaTempIn, state.unitConfiguration) && (
                      <Box sx={styles.outputFieldContainer}>
                        <Typography variant="caption">Temperature inlet</Typography>
                        <UnitAwareTextField 
                          unitType="temperature"
                          fullWidth 
                          metricValue={state.designSession![0].hotMediaTempIn} 
                          InputProps={{ 
                            readOnly: true,
                            endAdornment: temperatureUnit 
                          }}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {state.haveColdMediaOutputs && (
                  <Box sx={styles.coldMediaOutputs}>
                    <Typography variant="subtitle2" sx={styles.outputSectionTitle}>
                      {state.designSession![0].coldMediaType!} Outputs
                    </Typography>
                    {isRelevantOutputField(Constants.ColdMediaFlowrate, state.unitConfiguration) && (
                      <Box sx={styles.outputFieldContainer}>
                        <Typography variant="caption">Flow rate</Typography>
                        <UnitAwareTextField 
                          unitType={state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow'}
                          fullWidth 
                          metricValue={state.designSession![0].coldMediaFlowrate} 
                          InputProps={{ 
                            readOnly: true,
                            endAdornment: state.flowUnit 
                          }}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}

        {/* Action Buttons */}
        <Box sx={styles.actionButtonsContainer}>
          <Button
            variant="contained"
            color="primary"
            disabled={
              state.disableSubmit || 
              state.isLoading || 
              !validateFormCompletion() || // Form not completely filled
              state.formHasErrors // Any validation errors exist
            }
            sx={styles.calculateButton}
            onClick={() => submitForm()}
            startIcon={state.isLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          >
            {state.submitText}
          </Button>

          {state.designReceived && (
            <Box sx={styles.copyButtonContainer}>
              <Button
                variant="outlined"
                disabled={state.isLoading || state.disableCopyButton || state.isCopyLoading}
                sx={state.isCopyLoading ? styles.copyButtonLoading : styles.copyButton}
                onClick={() => copyToClipBoard()}
                startIcon={state.isCopyLoading ? <CircularProgress size={16} color="inherit" /> : <ContentCopyIcon />}
              >
                {state.isCopyLoading ? 'Copying...' : 'Copy'}
              </Button>

              {/* iOS workaround */}
              <Box
                component="input"
                onChange={() => {}} 
                sx={cssStyles.hiddenInput} 
                id='ios-copy' 
                value={state.iosContent}
                readOnly
                aria-hidden="true"
              />

              <Tooltip title={helpTooltipComponent} arrow>
                <IconButton color="primary">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        {state.showXmlMessage && state.xmlMessage && (
            <Box sx={styles.alertContainer}>
              <Alert severity={state.isXmlUsable ? "success" : "warning"}>
                {state.xmlMessage}
              </Alert>
            </Box>
          )}

        {state.showWarningForPHE && state.warningMessage && (
            <Box sx={styles.alertContainer}>
              <Alert severity="warning">
                {state.warningMessage}
              </Alert>
            </Box>
          )}

          {state.unitConfiguration && <FeedbackForm /> }
        
      </Box>
        </Box>

      </Box>

      {/* Confirmation Dialogs */}
      <Dialog
        open={state.showWarningDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_WARNING_DIALOG', payload: false })}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle sx={{fontSize: '1em'}} id="alert-dialog-title">
          {"Service Endpoint Unavailable"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{fontSize: '0.8em'}} id="alert-dialog-description">
            {state.warningMessage}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained"
            color="primary" onClick={() => dispatch({ type: 'SET_SHOW_WARNING_DIALOG', payload: false })} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* All warnings and information dialogs removed - content now shown in side panel */}
      
    
    </Container>
  );
}; 